/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IModuleStateManager, IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import get from 'lodash/get';
import * as _ from 'lodash';
import { computed, observable, reaction, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AttributeTextValue, Cart, CartLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy';

import { ICheckoutSectionContainerWPaymentSelectorData } from './checkout-section-container-w-payment-selector.data';
import { ICheckoutSectionContainerWPaymentSelectorProps } from './checkout-section-container-w-payment-selector.props.autogenerated';
import LoadingComponent from './components/loading';
import { calculateSurchargeAsync } from '../../actions/DataActionExtension.g';
import { IPaymentTenderType, PaymentProvider } from '../../utils/paymentContext';
import { PaymentSelectorButton } from '../../utils/paymentSelectorButton';

const paymentInstrumentModuleId = 'payment-instrument';
const customerAccountPaymentModuleId = 'customer-account-payment';
const zeroAmount = 0;

const CREDIT_CARD_TENDER_NAME = 'Credit Card';
export const ON_ACCOUNT_TENDER_NAME = 'ON ACCOUNT';
const PAYMENT_TYPE_ATTR_NAME = 'PaymentType';
const PAYMENT_DETAILS_ATTR_NAME = 'PaymentDetails';
const PAYMENT_TYPE_EXTENSION_PROPERTY_NAME = 'SelectedPaymentType';

enum TenderType {
    'Debit Card' = 'Debit',
    'ACH' = 'ACH'
}

enum CustAccountBlockedType {
    'No' = 0,
    'Invoice' = 1,
    'All' = 2
}

export interface ICheckoutSectionContainerWPaymentSelectorModuleProps
    extends ICheckoutSectionContainerWPaymentSelectorProps<ICheckoutSectionContainerWPaymentSelectorData>,
        IModuleStateProps {
    enableControl: boolean;
}

interface ICheckoutSectionContainerState {
    isCollapse?: boolean;
    paymentModules: Record<string, IPaymentTenderType[]>;
    selectedPayment: IPaymentTenderType | null;
    iframeReadyModules: Record<string, boolean>;
}

export interface ICheckoutSectionContainerItem {
    id: string;
    loading?: React.ReactNode;
    itemProps: INodeProps;
    item: React.ReactNode;
}

export interface ICheckoutSectionContainerViewProps extends ICheckoutSectionContainerWPaymentSelectorProps<{}> {
    className: string;
    checkoutPlainContainerProps: IModuleProps;
    items: ICheckoutSectionContainerItem[];
    renderSelectorButtons: React.ReactNode;
    renderSelectPaymentLink: React.ReactNode;
    selectedPaymentMethod: IPaymentTenderType | null;
    moduleState: IModuleStateManager;
    renderSaveButton: React.ReactNode;
    checkoutSectionContainerModuleProps: IModuleProps;
}

type AddressForAttribute = {
    Street: string | undefined;
    City: string | undefined;
    State: string | undefined;
    PostalCode: string | undefined;
    Country: string | undefined;
};

@observer
export class CheckoutPlainContainer extends React.Component<
    ICheckoutSectionContainerWPaymentSelectorModuleProps,
    ICheckoutSectionContainerState
> {
    @observable private isSurchargeProcessing: boolean = false;
    @observable private hasSurchargeProcessed: boolean = false;
    @observable private hasPaymentAttributeUpdated: boolean = false;

    @computed public get amountDue(): number {
        const {
            data: { checkout }
        } = this.props;

        if (!checkout?.result) {
            return 0;
        }

        const cart = checkout.result.checkoutCart?.cart;
        if (!cart || !cart.CartLines || !ArrayExtensions.hasElements(cart.CartLines)) {
            return 0;
        }

        return cart.TotalAmount ?? zeroAmount;
    }

    private get shouldEnableSCA(): boolean {
        return this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout ?? false;
    }

    private get isPaymentSectionContainer(): boolean {
        return ArrayExtensions.hasElements(this.props.moduleState.childIds.filter(childId => childId.includes(paymentInstrumentModuleId)));
    }

    public constructor(props: ICheckoutSectionContainerWPaymentSelectorModuleProps) {
        super(props);

        this.registerPaymentMethod = this.registerPaymentMethod.bind(this);
        this.setSelectedPayment = this.setSelectedPayment.bind(this);
        this.unsetPaymentMethod = this.unsetPaymentMethod.bind(this);
        this.reportIframeReady = this.reportIframeReady.bind(this);

        this.state = {
            isCollapse: false,
            paymentModules: {},
            selectedPayment: null,
            iframeReadyModules: {}
        };
    }

    public componentDidMount(): void {
        const items = this.getItems();
        const childIds = (items.map((item: React.ReactNode) => get(item, 'props.id')) as unknown) as string[];
        const shouldEnableSCA = this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout ?? false;

        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: shouldEnableSCA ? undefined : this.onCancel,
            onSubmit: shouldEnableSCA ? undefined : this.onSubmit,
            childIds
        });

        reaction(
            () => this.props.moduleState.isReady,
            () => {
                this.onContainerReady();
            }
        );

        reaction(
            () => this.props.moduleState.shouldSubmitContainer,
            () => {
                this.onSubmit();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.shouldCollapsePaymentSection,
            shouldCollapsePaymentSection => {
                if (shouldEnableSCA && this.isPaymentSectionContainer) {
                    this.setState({ isCollapse: shouldCollapsePaymentSection });
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.shouldTriggerPaymentWithPlaceOrder,
            () => {
                if (shouldEnableSCA && this.isPaymentSectionContainer) {
                    this.onSubmit();
                }
            }
        );

        reaction(
            () => this.props.moduleState.isReady,
            async isReady => {
                if (shouldEnableSCA && this.isPaymentSectionContainer && this.amountDue > zeroAmount) {
                    await this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                        newIsPaymentSectionContainerReady: isReady
                    });
                }
            }
        );

        reaction(
            () => this.props.moduleState.hasError,
            async hasError => {
                if (shouldEnableSCA && this.isPaymentSectionContainer) {
                    await this.props.data.checkout.result?.updateIsPaymentSectionContainerHasError({
                        newIsPaymentSectionContainerHasError: hasError
                    });
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isCheckoutCallFailed,
            async () => {
                if (this.isPaymentSectionContainer && this.props.data.checkout.result?.isCheckoutCallFailed) {
                    this.setState({ iframeReadyModules: {}, selectedPayment: null });
                    this.hasSurchargeProcessed = false;
                    this.hasPaymentAttributeUpdated = false;

                    this.onEdit();
                    await this.props.data.checkout.result.updateIsCheckoutCallFailed({ newIsCheckoutCallFailed: true });
                }
            }
        );

        reaction(
            () => {
                const cart = this.props.data.checkout?.result?.checkoutCart?.cart;
                const shippingState = cart?.ShippingAddress?.State ?? '';
                const attr: AttributeTextValue | undefined = (cart?.AttributeValues || []).find(a => a.Name === 'billing_address');
                const text = attr?.TextValue ?? '';

                let state = '';
                if (text) {
                    try {
                        const parsed = JSON.parse(text) as AddressForAttribute;
                        state = parsed.State ?? '';
                    } catch {
                        state = '';
                    }
                }

                return `${shippingState}::${state}`;
            },
            () => {
                this.unsetPaymentMethod();
            },
            { fireImmediately: false }
        );
    }

    public componentDidUpdate(): void {
        if (this.props.moduleState.hasExternalSubmitGroup) {
            const { childIds, hasError, getModule } = this.props.moduleState;
            const selectedModuleId = this.getSelectedPaymentModuleId();

            const hasReadyOrSkippedItems = childIds.some((childId: string) => {
                if (this.isPaymentChild(childId) && childId !== selectedModuleId) {
                    return false;
                }

                const state = getModule(childId);
                return !!state && (state.isReady || state.isSkipped);
            });

            if (hasReadyOrSkippedItems && hasError) {
                this.onEdit();
            }
        }

        this.managePaymentModuleVisibility();
    }

    public shouldComponentUpdate(
        nextProps: ICheckoutSectionContainerWPaymentSelectorModuleProps,
        nextState: ICheckoutSectionContainerState
    ): boolean {
        if (this.state !== nextState || this.props.data !== nextProps.data) {
            return true;
        }

        return false;
    }

    public render(): JSX.Element | null {
        const {
            enableControl,
            moduleState,
            config: { width },
            data: { customerInformation, invoiceAccountInfo }
        } = this.props;

        const items = this.getItems();
        if (items.length === 0) {
            this.props.context.telemetry.error('Checkout section content is empty, module wont render');
            return null;
        }

        const moduleClassName = classname(
            'checkout-section-container-w-payment-selector',
            {
                'has-initialized': moduleState.hasInitialized,
                'is-ready': moduleState.isReady,
                'is-updating': !moduleState.isReady,
                container: width === 'container',
                'col-12 fullwidth': width === 'fluid',
                'checkout-section-container-w-payment-selector--hide-payments': !this.state.selectedPayment
            },
            this.props.config.className
        );

        const isInvoiceAccountBlocked = invoiceAccountInfo.result?.Blocked || false;
        const invoiceAccountBlockedType = invoiceAccountInfo.result?.BlockedType || 0;

        const isOnAccountCust = customerInformation.result?.AllowOnAccountPayment || false;
        const isAccountBlocked = customerInformation.result?.Blocked || isInvoiceAccountBlocked;
        const accountBlockedType = Math.max(customerInformation.result?.BlockedType || 0, invoiceAccountBlockedType);

        const viewProps: ICheckoutSectionContainerViewProps = {
            ...this.props,
            selectedPaymentMethod: this.state.selectedPayment,
            renderSelectorButtons: this.renderSelectorButtons(isOnAccountCust, isAccountBlocked, accountBlockedType),
            renderSelectPaymentLink: this.renderSelectPaymentLink(),
            className: '',
            checkoutPlainContainerProps: {
                moduleProps: this.props,
                className: moduleClassName
            },
            checkoutSectionContainerModuleProps: {
                moduleProps: this.props,
                className: 'checkout-section-container-w-payment-selector'
            },
            items: items.map((item: React.ReactNode) => {
                const childId = (get(item, 'props.id') as unknown) as string;
                const state = moduleState.getModule(childId);

                const hidden =
                    this.state.isCollapse || (state.hasModuleState && (!state.hasInitialized || state.isDisabled || state.isSkipped));

                return {
                    id: childId,
                    loading: state.hasModuleState && !state.hasInitialized && <LoadingComponent />,
                    itemProps: {
                        'data-type-name': state.typeName,
                        className: classname('checkout-section-container-w-payment-selector__item', {
                            hidden,
                            initialized: state.hasInitialized,
                            disabled: state.isDisabled,
                            skipped: state.isSkipped,
                            ready: state.isReady,
                            updating: state.isUpdating,
                            pending: state.isPending
                        })
                    },
                    item: React.cloneElement(item as React.ReactElement, { enableControl })
                };
            }),
            renderSaveButton: this.renderSaveButton()
        };

        return (
            <PaymentProvider
                value={{
                    paymentModules: this.state.paymentModules,
                    selectedPayment: this.state.selectedPayment || ({} as IPaymentTenderType),
                    setSelectedPaymentState: this.setSelectedPayment,
                    registerPaymentMethod: this.registerPaymentMethod,
                    iframeReadyModules: this.state.iframeReadyModules,
                    reportIframeReady: this.reportIframeReady
                }}
            >
                {this.props.renderView(viewProps) as React.ReactElement}
            </PaymentProvider>
        );
    }

    private managePaymentModuleVisibility(): void {
        const { selectedPayment } = this.state;
        if (!selectedPayment) {
            return;
        }

        const selectedModuleId = this.getSelectedPaymentModuleId();
        if (!selectedModuleId) {
            return;
        }

        const { childIds, getModule } = this.props.moduleState;

        childIds.forEach(childId => {
            if (!this.isPaymentChild(childId)) {
                return;
            }

            const state = getModule(childId);
            if (!state || !state.hasModuleState || !state.hasInitialized) {
                return;
            }

            if (childId === selectedModuleId) {
                if (state.isSkipped) {
                    state.onUpdating();
                }
            } else {
                if (!state.isSkipped && !state.isDisabled) {
                    state.onSkip();
                }
            }
        });
    }

    private registerPaymentMethod(elementId: string, tenderTypeList: IPaymentTenderType[]): void {
        this.setState(prevState => ({
            paymentModules: { ...prevState.paymentModules, [elementId]: tenderTypeList }
        }));
    }

    private reportIframeReady(elementId: string): void {
        this.setState(prevState => ({
            iframeReadyModules: { ...prevState.iframeReadyModules, [elementId]: true }
        }));
    }

    public setSelectedPayment = async (paymentType: IPaymentTenderType): Promise<void> => {
        if (this.shouldEnableSCA && this.isPaymentSectionContainer) {
            this.props.moduleState.onUpdating();
            await this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                newIsPaymentSectionContainerReady: false
            });
        }

        const checkout = await this.props.data.checkout;
        const checkoutCart = checkout.checkoutCart;

        let shouldRefreshCart = false;
        let updatedAttr: AttributeTextValue[] | undefined;
        let updatedCartlines;

        if (!this.hasPaymentAttributeUpdated || paymentType.tenderTypeId !== this.state.selectedPayment?.tenderTypeId) {
            this.hasSurchargeProcessed = false;
            updatedAttr = this.updatePaymentTypeAttribute(paymentType);
            this.hasPaymentAttributeUpdated = true;

            if (checkoutCart.hasInvoiceLine) {
                updatedCartlines = this.updatePaymentDetailsAttribute(checkoutCart.cart);
            }
        }

        this.setState({ selectedPayment: paymentType });

        if (updatedAttr) {
            await checkoutCart.updateAttributeValues({ newAttributeValues: updatedAttr });
            await checkoutCart.updateExtensionProperties({ newExtensionProperties: this.updatePaymentTypeExtensionProperty(paymentType) });
            shouldRefreshCart = true;
        }

        if (!this.isSurchargeProcessing) {
            try {
                runInAction(() => {
                    this.isSurchargeProcessing = true;
                });

                if (!this.hasSurchargeProcessed) {
                    await this.calculateSurcharge();
                    shouldRefreshCart = true;
                }

                if (updatedCartlines) {
                    await checkoutCart.updateCartLines({ updatedCartLinesObject: updatedCartlines });
                    shouldRefreshCart = true;
                }
            } finally {
                runInAction(() => {
                    this.isSurchargeProcessing = false;
                });
            }
        }

        if (shouldRefreshCart) {
            await checkoutCart.refreshCart({});
        }
    };

    private unsetPaymentMethod = async (): Promise<void> => {
        this.hasSurchargeProcessed = false;
        this.hasPaymentAttributeUpdated = false;

        const { childIds, getModule } = this.props.moduleState;
        childIds.forEach(childId => {
            if (!this.isPaymentChild(childId)) {
                return;
            }

            const state = getModule(childId);
            if (state?.isSkipped) {
                state.onUpdating();
            }
        });

        this.setState({ selectedPayment: null });
        this.props.moduleState.onUpdating();

        if (this.shouldEnableSCA && this.isPaymentSectionContainer) {
            await this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                newIsPaymentSectionContainerReady: false
            });
        }
    };

    public renderSelectorButtons = (isOnAccountCust: boolean, isAccountBlocked: boolean, accountBlockedType: number): React.ReactNode => {
        const containsInvoice = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        const paymentModulesAsArray = this.getPaymentModulesAsArray();

        const filteredPaymentModules = paymentModulesAsArray.filter(p => {
            if (containsInvoice) {
                if (p.showOnInvoiceCheckout) {
                    return p.tenderName.toUpperCase() !== ON_ACCOUNT_TENDER_NAME;
                }
            }

            if (!containsInvoice) {
                if (p.showOnNormalPartsCheckout) {
                    if (isOnAccountCust) {
                        return p.tenderName.toUpperCase() === ON_ACCOUNT_TENDER_NAME;
                    }

                    return p.tenderName.toUpperCase() !== ON_ACCOUNT_TENDER_NAME;
                }
            }

            return false;
        });

        const shouldBlockCheckout = (): boolean => {
            if (this.isBelowMinimumWithCredit()) {
                this.props.moduleState.onUpdating();
                return true;
            }

            if (!isAccountBlocked) {
                return false;
            }

            switch (accountBlockedType) {
                case CustAccountBlockedType.Invoice:
                    return !containsInvoice;
                case CustAccountBlockedType.All:
                    return true;
                case CustAccountBlockedType.No:
                    return false;
                default:
                    return true;
            }
        };

        const sortedFilteredPaymentModules = containsInvoice
            ? filteredPaymentModules.sort((a, b) => a.tenderName.localeCompare(b.tenderName))
            : filteredPaymentModules;

        const showBelowMinimumMessage = this.isBelowMinimumWithCredit();
        const belowMinimumMessage = this.props.context.app.config.belowMinimumMessage || this.props.resources.belowMinimumFallbackMessage;

        const hasIframeBasedPayments = sortedFilteredPaymentModules.some(p => p.tenderName.toUpperCase() !== ON_ACCOUNT_TENDER_NAME);

        return (
            <div
                className={classname('checkout-section-container-w-payment-selector__button-container', {
                    'checkout-section-container-w-payment-selector--hide-buttons': !!this.state.selectedPayment
                })}
            >
                {showBelowMinimumMessage ? (
                    <div className='checkout-section-container-w-payment-selector__button-container-error'>{belowMinimumMessage}</div>
                ) : !this.areAllIframesReady() && hasIframeBasedPayments ? (
                    <LoadingComponent />
                ) : sortedFilteredPaymentModules.length > 0 ? (
                    sortedFilteredPaymentModules.map(option => (
                        <PaymentSelectorButton
                            paymentTenderType={option}
                            onTenderTypeSelect={this.setSelectedPayment}
                            key={option.tenderTypeId}
                            disabled={shouldBlockCheckout()}
                        />
                    ))
                ) : (
                    <div className='checkout-section-container-w-payment-selector__button-container-error'>
                        {this.props.resources.errorLoadingPaymentOptions}
                    </div>
                )}
            </div>
        );
    };

    public renderSelectPaymentLink = (): React.ReactNode => {
        const { selectPaymentLinkText } = this.props.resources;

        return (
            <div
                className={classname('checkout-section-container-w-payment-selector__select-payment-container', {
                    'checkout-section-container-w-payment-selector--hide-buttons': !this.state.selectedPayment
                })}
            >
                <button
                    className='checkout-section-container-w-payment-selector__select-payment-link msc-btn btn-link'
                    onClick={() => void this.unsetPaymentMethod()}
                >
                    {selectPaymentLinkText}
                </button>
            </div>
        );
    };

    public renderSaveButton = (): React.ReactNode => {
        if (this.shouldEnableSCA) {
            return;
        }
        const { saveButtonLabel } = this.props.resources;
        const element = document.getElementsByClassName('checkout-section-container-w-payment-selector__button-container');
        if (element && element[0]) {
            const guidedFormContainer = element[0].closest('.ms-checkout__guided-card-content');
            const saveAndContinueButtonFooter =
                guidedFormContainer && guidedFormContainer.nextSibling ? guidedFormContainer.nextSibling : null;

            if (saveAndContinueButtonFooter) {
                // @ts-expect-error
                saveAndContinueButtonFooter.classList.add('checkout-section-container-w-payment-selector--hide-save-button');
            }
        }
        return (
            <div
                className={classname('checkout-section-container-w-payment-selector__save-container', {
                    'checkout-section-container-w-payment-selector--hide-buttons': !this.state.selectedPayment
                })}
            >
                <button className='checkout-section-container-w-payment-selector__save-button msc-btn btn-primary' onClick={this.onClick}>
                    {saveButtonLabel}
                </button>
            </div>
        );
    };

    private readonly onClick = async (): Promise<void> => {
        this.getActiveChildModuleStates().forEach(state => {
            !state.isReady && state.onSubmit && state.onSubmit();
        });

        this.onReady();

        if (this.shouldEnableSCA) {
            await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
        }
    };

    /**
     * Sets the module state to ready.
     */
    private readonly onReady = () => {
        this.props.moduleState.onReady();
    };

    private readonly getItems = (): React.ReactNode[] => {
        const { slots } = this.props;
        return (slots && slots.primary && slots.primary.length && slots.primary) || [];
    };

    private readonly getActiveChildModuleStates = (): IModuleStateManager[] => {
        const { childIds, getModule } = this.props.moduleState;
        return childIds
            .map(getModule)
            .filter((state): state is IModuleStateManager => !!state && !state.isDisabled && state.hasModuleState);
    };

    private isPaymentChild(childId: string): boolean {
        return childId.includes(paymentInstrumentModuleId) || childId.includes(customerAccountPaymentModuleId);
    }

    private getSelectedPaymentModuleId(): string {
        const { selectedPayment, paymentModules } = this.state;
        if (!selectedPayment) {
            return '';
        }

        for (const [moduleId, tenders] of Object.entries(paymentModules)) {
            if ((tenders ?? []).some(t => t.tenderName === selectedPayment.tenderName)) {
                return moduleId;
            }
        }

        return '';
    }

    private getPaymentModulesAsArray = (): IPaymentTenderType[] =>
        _.flatten(Object.keys(this.state.paymentModules).map(key => this.state.paymentModules[key]));

    private areAllIframesReady(): boolean {
        const { paymentModules, iframeReadyModules } = this.state;
        const registeredIds = Object.keys(paymentModules);

        if (registeredIds.length === 0) {
            return false;
        }

        return registeredIds.every(moduleId => !!iframeReadyModules[moduleId]);
    }

    private isBelowMinimumWithCredit(): boolean {
        const cart = this.props.data.checkout.result?.checkoutCart.cart;
        const cartLines = cart?.CartLines ?? [];
        const hasCreditApplied = cartLines.some(line => line.InvoiceId?.startsWith('CN') || line.InvoiceId?.startsWith('FTC'));
        const subtotal = cart?.SubtotalAmount || 0;

        return hasCreditApplied && subtotal <= 0;
    }

    private calculateSurcharge = async (): Promise<void> => {
        const cartId = (await this.props.data.checkout).checkoutCart.cart.Id;

        if (this.state.selectedPayment && !this.hasSurchargeProcessed) {
            await calculateSurchargeAsync(
                { callerContext: this.props.context.actionContext },
                cartId,
                +this.state.selectedPayment.tenderTypeId
            );

            runInAction(() => {
                this.hasSurchargeProcessed = true;
            });
        }
    };

    private updatePaymentTypeAttribute = (paymentType: IPaymentTenderType | null): AttributeTextValue[] | undefined => {
        if (!paymentType) {
            return;
        }

        const paymentTypeAttribute = {
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
            Name: PAYMENT_TYPE_ATTR_NAME,
            ExtensionProperties: [],
            TextValue:
                paymentType.tenderName === CREDIT_CARD_TENDER_NAME
                    ? null
                    : TenderType[paymentType.tenderName as keyof typeof TenderType] || null,
            TextValueTranslations: []
        };

        const cart = this.props.data.checkout.result?.checkoutCart.cart;
        const attributeArr = [...(cart?.AttributeValues || [])];
        const idx = attributeArr.findIndex(attr => attr.Name === PAYMENT_TYPE_ATTR_NAME);

        if (idx !== -1) {
            attributeArr.splice(idx, 1, paymentTypeAttribute);
        } else {
            attributeArr.push(paymentTypeAttribute);
        }

        runInAction(() => {
            this.hasPaymentAttributeUpdated = true;
        });

        return attributeArr;
    };

    private updatePaymentDetailsAttribute = (checkoutCart: Cart): CartLine[] | undefined => {
        if (!checkoutCart) {
            return;
        }

        const linesToUpdate = checkoutCart.CartLines?.filter(cartLine => {
            return !cartLine.InvoiceId!.startsWith('CN') && !cartLine.InvoiceId!.startsWith('FTC');
        }).map(cartLine => {
            if (!cartLine.InvoiceId) {
                return cartLine;
            }
            const existingAttribute: AttributeTextValue[] = cartLine.AttributeValues || [];
            const idx = existingAttribute.findIndex(attr => attr.Name === PAYMENT_DETAILS_ATTR_NAME);

            const paymentDetailsAttribute = {
                Name: PAYMENT_DETAILS_ATTR_NAME,
                TextValue: cartLine.ExtendedPrice?.toString(),
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue'
            } as AttributeTextValue;

            if (idx !== -1) {
                existingAttribute.splice(idx, 1, paymentDetailsAttribute);
            } else {
                existingAttribute.push(paymentDetailsAttribute);
            }

            return { ...cartLine, AttributeValues: existingAttribute };
        });

        return linesToUpdate && linesToUpdate.length > 0 ? linesToUpdate : undefined;
    };

    private updatePaymentTypeExtensionProperty = (paymentType: IPaymentTenderType | null): CommerceProperty[] => {
        const cart = this.props.data.checkout.result?.checkoutCart.cart;
        const extensionProperties = [...(cart?.ExtensionProperties || [])];

        const paymentTypeExtensionProperty: CommerceProperty = {
            Key: PAYMENT_TYPE_EXTENSION_PROPERTY_NAME,
            Value: {
                BooleanValue: paymentType ? true : false
            }
        };

        const idx = extensionProperties.findIndex(prop => prop.Key === PAYMENT_TYPE_EXTENSION_PROPERTY_NAME);

        if (idx !== -1) {
            extensionProperties.splice(idx, 1, paymentTypeExtensionProperty);
        } else {
            extensionProperties.push(paymentTypeExtensionProperty);
        }

        return extensionProperties;
    };

    private readonly clearError = (): void => {
        this.getActiveChildModuleStates().forEach(state => {
            state.hasError && state.setHasError(false);
        });
    };

    private readonly onSubmit = (): void => {
        this.props.telemetry.information('Section container onSubmit is called.');

        const selectedModuleId = this.getSelectedPaymentModuleId();

        this.clearError();

        const { childIds, getModule } = this.props.moduleState;
        childIds.forEach(childId => {
            const state = getModule(childId);
            if (!state || state.isDisabled || !state.hasModuleState) {
                return;
            }

            if (this.isPaymentChild(childId) && childId !== selectedModuleId) {
                return;
            }

            !state.isReady && !state.isSkipped && state.onSubmit && state.onSubmit();
        });

        if (this.shouldEnableSCA && this.isPaymentSectionContainer && this.amountDue === zeroAmount) {
            this.props.data.checkout.result?.updateIsPaymentSectionContainerReady({
                newIsPaymentSectionContainerReady: true
            });
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Section container onCancel is called.');

        this.clearError();
        this.getActiveChildModuleStates().forEach(state => {
            !state.isReady && state.onCancel && state.onCancel();
        });
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Section container onEdit is called.');

        const selectedModuleId = this.getSelectedPaymentModuleId();
        const { childIds, getModule } = this.props.moduleState;

        childIds.forEach(childId => {
            const state = getModule(childId);
            if (!state || state.isDisabled || !state.hasModuleState) {
                return;
            }

            if (this.isPaymentChild(childId) && childId !== selectedModuleId && state.isSkipped) {
                return;
            }

            (state.isReady || state.isSkipped) && state.onEdit && state.onEdit();
        });
    };

    private readonly onContainerReady = (): void => {
        this.props.telemetry.information('Section container onContainerReady is called.');

        const selectedModuleId = this.getSelectedPaymentModuleId();
        const { childIds, getModule } = this.props.moduleState;

        childIds.forEach(childId => {
            const state = getModule(childId);
            if (!state || state.isDisabled || !state.hasModuleState) {
                return;
            }

            if (this.isPaymentChild(childId) && childId !== selectedModuleId) {
                return;
            }

            state.isReady && state.onContainerReady && state.onContainerReady();
        });
    };
}

export default withModuleState(CheckoutPlainContainer);
